import {
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  get,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";
import React, { useState, type FocusEvent } from "react";
import {
  SelectV2,
  type SelectV2Props,
} from "src/components/SelectV2/SelectV2.tsx";
import { useDebouncedValue } from "@exonn/hooks";
import type { SelectServerOptions } from "src/components/Form/Form.types.ts";
import { useInfiniteSelect } from "src/system/useInfiniteSelect.ts";
import { useSelect } from "src/system/useSelect.tsx";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { ResourceListModal } from "src/components/ResourceListModal/ResourceListModal.tsx";
import { useServerSelectFilter } from "src/system/useServerSelectFilter.ts";
import { useSelectOptions } from "src/system/useSelectOptions";
import { useSelectFieldOnChange } from "src/components/Form/fields/hooks/useSelectFieldOnChange.ts";
import { useSelectFieldFallbackFilter } from "src/components/Form/fields/hooks/useSelectFieldFallbackFilter.ts";
import type {
  GridRowParams,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { getSelectValue } from "src/system/getSelectValue.ts";
import {
  isInIframe,
  replacePathParams,
  trimInfiniteQuery,
  unique,
} from "@exonn/utils";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import { useRouter } from "src/lib/react-router/useRouter.ts";
import { DocumentTypes } from "@exonn/js-sdk";
import { ResourceEnum } from "src/constants/constants.ts";
import { getDefaultSelectOptions } from "src/system/getDefaultSelectOptions.ts";

export type SelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  forceValue?: any;
  locale?: string;
  serverOptions?: SelectServerOptions;
  fallbackResources?: GridValidRowModel[];
  getInitialOptionsFromMainResource?: boolean;
  onChange?: (value: string, resource: any, form: any) => void;
} & Omit<SelectV2Props, "onChange"> &
  Omit<ControllerProps<TFieldValues, TName>, "render">;

const DEFAULT_PAGE_SIZE = 15;

// todo: forward ref
export const SelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: SelectFieldProps<TFieldValues, TName>,
) => {
  const {
    name,
    disabled,
    options: optionsProp,
    onChange: onChangeProp,
    locale: lang,
    onBlur: onBlurProp,
    serverOptions: serverOptionsProp,
    forceValue,
    isMultiple,
    fallbackResources,
    getInitialOptionsFromMainResource = false,
    showAdvancedSearch,
    ...restComponentProps
  } = props;

  const router = useRouter();

  const [isOpen, setIsOpen] = useState(false);
  const [isAdvancedSearchOpen, setAdvancedSearchOpen] = useState(false);

  const form = useFormContext();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 500);

  const serverOptions = serverOptionsProp
    ? {
        ...getDefaultSelectOptions({
          resource: serverOptionsProp?.resource,
        }),
        ...serverOptionsProp,
      }
    : undefined;

  const serverOptionsEnabled = !!serverOptions;

  const filter = useServerSelectFilter(
    {
      ...serverOptions,
    },
    false,
  );

  const filterModal = useServerSelectFilter(
    {
      ...serverOptions,
    },
    true,
  );

  const filter2 = useServerSelectFilter(
    {
      filter: serverOptions?.filter2,
      logicOperator: serverOptions?.logicOperator2,
    },
    false,
  );

  const filter2Modal = useServerSelectFilter(
    {
      filter: serverOptions?.filter2,
      logicOperator: serverOptions?.logicOperator2,
    },
    true,
  );

  const value = useWatch({
    name: name,
  });

  const infiniteSelect = useInfiniteSelect({
    locale: lang,
    resource: serverOptions ? serverOptions.resource : "",
    search: debouncedSearch,
    include: serverOptions?.include,
    searchField: serverOptions?.searchField,
    sort: serverOptions?.sort,
    filter: filter,
    filter2: filter2,
    useSimplePagination: true,
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
    pageSize: DEFAULT_PAGE_SIZE,
    queryOptions: {
      enabled: serverOptionsEnabled && isOpen,
      refetchOnMount: false,
      gcTime: 0,
      staleTime: 0,
      placeholderData: keepPreviousData,
    },
  });

  const hasValue = isMultiple
    ? Array.isArray(value) && value.length > 0
    : value != null && value !== "";

  const fallbackFilter = useSelectFieldFallbackFilter({
    serverOptions,
    value,
  });

  const cachedResources = form.getValues(`__cache.${name}_resources`);

  const fallbackOptionsFromFormState = useSelectOptions({
    resources: cachedResources || [],
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
  });

  const shouldLoadFallbackOptions =
    serverOptionsEnabled &&
    hasValue &&
    !getInitialOptionsFromMainResource &&
    !fallbackResources;

  const fallbackOptionsSelect = useSelect({
    resource: serverOptions ? serverOptions.resource : "",
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
    locale: lang,
    include: serverOptions?.include,
    filter: fallbackFilter,
    queryOptions: {
      enabled: shouldLoadFallbackOptions,
      placeholderData: keepPreviousData,
    },
  });

  const customFallbackOptions = useSelectOptions({
    resources: fallbackResources || [],
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && serverOptions) {
      trimInfiniteQuery(queryClient, infiniteSelect.queryKey);
      infiniteSelect.query.refetch();

      if (shouldLoadFallbackOptions) {
        fallbackOptionsSelect.query.refetch();
      }
    }
  };

  const handleScrollEnd = () => {
    if (!infiniteSelect.query.isFetchingNextPage) {
      infiniteSelect.query.fetchNextPage({
        cancelRefetch: false,
      });
    }
  };

  const handleChange = useSelectFieldOnChange({
    onChange: onChangeProp,
    name,
    isMultiple,
    getInitialOptionsFromMainResource,
    valueProp: serverOptions?.valueProp,
  });

  const options = serverOptions ? infiniteSelect.options : optionsProp;

  const isLoadingData = serverOptions
    ? infiniteSelect.query.isFetching &&
      !infiniteSelect.query.isFetchingNextPage
    : false;

  const handleBlur = (event: FocusEvent) => {
    onBlurProp?.(event);
  };

  const fallbackOptions = [
    ...fallbackOptionsFromFormState,
    ...fallbackOptionsSelect.options,
    ...customFallbackOptions,
  ];
  const isLoadingSelectedOption = getInitialOptionsFromMainResource
    ? false
    : fallbackOptionsSelect.query.isLoading;

  const isLoadingMoreOptions = infiniteSelect.query.isFetchingNextPage;

  const handleRowClick = (params: GridRowParams) => {
    const onChange = (value: any) => {
      form.setValue(name, value, {
        shouldDirty: true,
      });
    };
    const value = getSelectValue({
      item: params.row._resource,
      valueProp: serverOptions?.valueProp,
    });
    const nextValue = isMultiple
      ? unique([...(form.getValues(name) ?? []), value])
      : value;
    handleChange({
      onChange: onChange,
      resources: [params.row._resource],
    })(nextValue);
  };

  const handleSearchChange = (value: string) => {
    const queryKey = infiniteSelect.getQueryKey({
      search: value,
    });
    trimInfiniteQuery(queryClient, queryKey);
    setSearch(value);
  };

  const resolvedValue = forceValue != null ? forceValue : value;
  const resources = [
    ...(cachedResources || []),
    ...(serverOptions ? fallbackOptionsSelect.resources : []),
    ...infiniteSelect.resources,
    ...(fallbackResources || []),
  ];
  const resource = resources.find((resource) => {
    return (
      getSelectValue({
        item: resource,
        valueProp: serverOptions?.valueProp,
      }) === resolvedValue
    );
  });
  const handleViewButton = () => {
    if (serverOptions && resolvedValue && resource) {
      if (
        isInIframe() &&
        serverOptions.resource === ResourceEnum.AccountingAccount &&
        resource.oxid &&
        resource.number
      ) {
        window.parent.postMessage(
          {
            eventName: "OPEN_ENTITY",
            payload: {
              tab: "mxusers2",
              view: "exonn_customermain",
              oxid: resource.oxid,
              number: resource.number,
            },
          },
          "https://exonn.online",
        );
        return;
      }
      if (
        isInIframe() &&
        serverOptions.resource === ResourceEnum.Document &&
        resource.oxid &&
        resource.document_number
      ) {
        window.parent.postMessage(
          {
            eventName: "OPEN_ENTITY",
            payload: {
              tab:
                resource.document_type === DocumentTypes.Invoice
                  ? "amazondocumentsrechnungen_rechnung"
                  : "amazondocumentsrechnungen_gutschrift",
              view: "exonn_documentsrechnungmain",
              oxid: resource?.oxid,
              number: resource?.document_number,
            },
          },
          "https://exonn.online",
        );
        return;
      }
      if (
        isInIframe() &&
        serverOptions.resource === ResourceEnum.Supplier &&
        resource.oxid &&
        resource.number
      ) {
        window.parent.postMessage(
          {
            eventName: "OPEN_ENTITY",
            payload: {
              tab: "mxvendor",
              view: "exonn_vendor_main",
              oxid: resource.oxid,
              number: resource.number,
            },
          },
          "https://exonn.online",
        );
        return;
      }

      if (serverOptions.resource === ResourceEnum.AccountingPostingPosition) {
        const paths = createModuleRoutePaths({
          resource: ResourceEnum.AccountingPosting,
        });
        router.navigate(
          replacePathParams(paths.edit.root, {
            id: resource?.accounting_posting_id,
          }),
        );
        return;
      }

      const routePaths = createModuleRoutePaths({
        resource: serverOptions.resource,
      });

      router.navigate(
        replacePathParams(routePaths.edit.root, {
          id: resource.id,
        }),
      );
    }
  };

  const getCopyText = () => {
    if (resolvedValue && serverOptions && resource) {
      return serverOptions.getCopyText?.(resource);
    }
    return "";
  };

  const { errors } = useFormState();

  return (
    <>
      <SelectV2
        value={resolvedValue}
        name={name}
        showButtons={resolvedValue && serverOptions?.showButtons}
        getCopyText={getCopyText}
        onViewButton={handleViewButton}
        fallbackOptions={fallbackOptions}
        showSearch={true}
        isLoadingMoreOptions={isLoadingMoreOptions}
        onScrollEnd={handleScrollEnd}
        isLoadingSelectedOption={isLoadingSelectedOption}
        onOpenChange={handleOpenChange}
        isLoadingData={isLoadingData}
        disabled={disabled}
        isOpen={isOpen}
        isMultiple={isMultiple}
        onChange={handleChange({
          onChange: (value) => {
            form.setValue(name, value, {
              shouldDirty: true,
            });
          },
          resources: infiniteSelect.resources,
        })}
        searchValue={search}
        onSearchValueChange={handleSearchChange}
        options={options}
        onBlur={handleBlur}
        error={get(errors?.root || {}, name)?.message}
        showAdvancedSearch={showAdvancedSearch}
        onAdvancedSearchOpen={setAdvancedSearchOpen}
        {...restComponentProps}
      />
      {serverOptions?.resource && (
        <ResourceListModal
          resource={serverOptions?.resource}
          withoutDescendants={serverOptions?.withoutDescendants}
          isModalOpen={isAdvancedSearchOpen}
          onModalOpen={setAdvancedSearchOpen}
          filter={filterModal}
          filter2={filter2Modal}
          include={serverOptions?.include}
          onRowClick={handleRowClick}
        />
      )}
    </>
  );
};
